// pkg144 — Adoption Documents panel (adoption_stepparent tab): signing-attorney select, attorney-fill fields saved to attorney_data, four generators (petition / registry inquiry / consent-when-14+ / decree), ICWA hold banner driven by the intake's icwa answer + 409 ICWA_HOLD; AdoptionIntakePage joins SUPPORTED in admin mode — delivered 2026-08-11
// pkg127 — per-tab download / email-to-staff bar via the shared ModuleSendControl (all six data modules; calculator excluded per D-126d); full staff roster loaded for the picker — delivered 2026-07-27
// pkg125a — BUG FIX: getClientModules returns { client, modules }, not an array (pkg125 read it bare → every client showed "No built-in modules"); + ?m=<key> deep link so Overview Form Activity tiles open a specific module tab — delivered 2026-07-27
// pkg125 — Client Modules workspace (D-124c/f): the firm opens a client and
// works their portal modules directly. A tab strip shows EXACTLY the built-in
// modules assigned to this client (same server-side jurisdiction / practice-
// area / firm-choice filters as the client's own sidebar); each tab renders
// the SAME page component the client uses, in admin mode (adminApi I/O,
// Submit hidden — D-124e). The Case Caption card stores the caption on the
// client (pkg124 columns), and the budget / witness / asset-ledger tabs carry
// a Generate Pleading panel producing captioned .docx filings with the firm
// signature block + Certificate of Service. Titles carry the client's party
// status; the Income & Expense Worksheet generates per-period via two buttons
// (Pre-Divorce / Post-Divorce) per Ryan's D-124f ruling.

import React, { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import ModuleSendControl from '../components/ModuleSendControl'; // pkg127

import BudgetPage        from './BudgetPage';
import WitnessPage       from './WitnessPage';
import DemographicPage   from './DemographicPage';
import ParentingPlanPage from './ParentingPlanPage';
import JuvenileInfoPage  from './JuvenileInfoPage';
import AssetLedgerPage   from './AssetLedgerPage';
import ChildSupportPage  from './ChildSupportPage';
import AdoptionIntakePage from './AdoptionIntakePage'; // pkg144

// The built-in modules this workspace can host. Content/PDF and AI modules
// have their own admin surfaces (Document Modules); discovery_response has
// the Discovery tab — neither belongs here.
const SUPPORTED = {
  juvenile_info:      { component: JuvenileInfoPage,  pleading: null },
  demographic:        { component: DemographicPage,   pleading: null },
  budget:             { component: BudgetPage,        pleading: 'income-expense' },
  parenting_plan:     { component: ParentingPlanPage, pleading: null },
  child_support_calc: { component: ChildSupportPage,  pleading: null, stateless: true },
  witnesses:          { component: WitnessPage,       pleading: 'witness-list' },
  asset_ledger:       { component: AssetLedgerPage,   pleading: 'asset-liability' },
  adoption_stepparent: { component: AdoptionIntakePage, pleading: null, adoption: true }, // pkg144
};

const EMPTY_CAPTION = {
  caption_court: '', caption_plaintiff: '', caption_defendant: '',
  caption_docket_no: '', caption_division: '', client_party: '',
  opposing_counsel: '',
};

export default function AdminClientModulesPage() {
  const { id } = useParams();
  // pkg125a — ?m=<module key> opens the workspace on a specific module (the
  // Overview Form Activity tiles link here); the active tab is kept in the
  // URL so refresh and back/forward hold position, like ?tab= on the detail
  // page.
  const [searchParams, setSearchParams] = useSearchParams();

  const [client, setClient]     = useState(null);
  const [tabs, setTabs]         = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [attorneys, setAttorneys] = useState([]);
  const [allStaff, setAllStaff] = useState([]); // pkg127 — staff-send picker roster
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState('');

  // Caption card state
  const [caption, setCaption]       = useState(EMPTY_CAPTION);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [savingCaption, setSavingCaption] = useState(false);
  const [captionStatus, setCaptionStatus] = useState('');

  // Pleading panel state
  const [attorneyIds, setAttorneyIds] = useState([]);
  const [signingAttorneyId, setSigningAttorneyId] = useState(null);
  const [generating, setGenerating] = useState('');
  const [pleadError, setPleadError] = useState('');
  const [pleadStatus, setPleadStatus] = useState('');

  // pkg144 — Adoption Documents panel state: intake meta (ICWA flag +
  // computed child-14+ flag from the admin GET), the attorney-fill fields,
  // and the signing-attorney selection (single signer on adoption documents,
  // parenting-plan style — separate from the multi-attorney pleading list).
  const [adoptMeta, setAdoptMeta] = useState(null);
  const [adoptFill, setAdoptFill] = useState({ filing_fee: '', attorney_fee: '', judge_name: '', hearing_date: '', petition_filed_date: '' });
  const [adoptSigner, setAdoptSigner] = useState(null);
  const [adoptSavingFill, setAdoptSavingFill] = useState(false);
  const [adoptFillStatus, setAdoptFillStatus] = useState('');

  useEffect(() => {
    if (activeKey !== 'adoption_stepparent') return;
    adminApi.getClientAdoptionIntake(id).then((rec) => {
      setAdoptMeta(rec || { data: {}, child_is_14: false });
      if (rec && rec.attorney_data) {
        setAdoptFill((f) => ({ ...f, ...rec.attorney_data }));
      }
    }).catch(() => setAdoptMeta({ data: {}, child_is_14: false }));
  }, [activeKey, id]);

  useEffect(() => {
    Promise.all([
      adminApi.getClient(id),
      adminApi.getClientModules(id),
      adminApi.getAdminUsers(),
    ]).then(([cli, mods, users]) => {
      setClient(cli);
      setCaption({
        caption_court:     cli.caption_court     || '',
        caption_plaintiff: cli.caption_plaintiff || '',
        caption_defendant: cli.caption_defendant || '',
        caption_docket_no: cli.caption_docket_no || '',
        caption_division:  cli.caption_division  || '',
        client_party:      cli.client_party      || '',
        opposing_counsel:  cli.opposing_counsel  || '',
      });
      // A caption that's never been set starts the card open so it gets filled.
      if (!cli.caption_plaintiff && !cli.caption_defendant) setCaptionOpen(true);

      // pkg125a BUG FIX: GET /api/modules/admin/clients/:id returns
      // { client, modules } (see routes/modules.js + the detail page's
      // data.modules read) — pkg125 treated the response as a bare array,
      // which collapsed to [] and rendered the empty state for every client.
      const modList = Array.isArray(mods) ? mods : (mods && mods.modules) || [];
      const assigned = modList
        .filter((m) => m.enabled && SUPPORTED[m.key])
        .map((m) => ({ key: m.key, label: m.label }));
      setTabs(assigned);
      // Read the deep-link param straight off the URL — referencing the
      // searchParams hook value here would trip react-hooks/exhaustive-deps
      // (a CI=true build failure) or re-run the loader on every tab click.
      const requested = new URLSearchParams(window.location.search).get('m');
      if (requested && assigned.some((t) => t.key === requested)) {
        setActiveKey(requested);
      } else if (assigned.length) {
        setActiveKey(assigned[0].key);
      }

      setAllStaff(users || []); // pkg127 — every role; recipients firm-validated server-side
      // Owners + attorneys for the signature block — same derivation as the
      // Discovery generator (owners first, alphabetical by last name).
      const filtered = (users || [])
        .filter((u) => u.role === 'owner' || u.role === 'attorney')
        .sort((a, b) => {
          const roleOrder = { owner: 0, attorney: 1 };
          const ra = roleOrder[a.role] ?? 9;
          const rb = roleOrder[b.role] ?? 9;
          if (ra !== rb) return ra - rb;
          const lastA = (a.name || '').trim().split(/\s+/).pop().toLowerCase();
          const lastB = (b.name || '').trim().split(/\s+/).pop().toLowerCase();
          return lastA.localeCompare(lastB);
        });
      setAttorneys(filtered);
      const defaultIds = filtered.filter((u) => u.role === 'owner' && u.bar_number).map((u) => u.id);
      setAttorneyIds(defaultIds);
      if (defaultIds.length) setSigningAttorneyId(defaultIds[0]);

      setLoading(false);
    }).catch((err) => { setError(err.message); setLoading(false); });
  }, [id]);

  function setCap(key, val) {
    setCaption((c) => ({ ...c, [key]: val }));
    setCaptionStatus('');
  }

  async function handleSaveCaption() {
    setSavingCaption(true); setCaptionStatus(''); setError('');
    try {
      await adminApi.saveClientCaption(id, caption);
      setCaptionStatus('Caption saved.');
    } catch (err) { setError(err.message); }
    finally { setSavingCaption(false); }
  }

  function toggleAttorney(attorneyId) {
    setAttorneyIds((prev) => {
      const next = prev.includes(attorneyId) ? prev.filter((x) => x !== attorneyId) : [...prev, attorneyId];
      if (!next.includes(signingAttorneyId)) setSigningAttorneyId(next[0] || null);
      return next;
    });
  }

  async function generatePleading(kind, period) {
    setPleadError(''); setPleadStatus('');
    if (!caption.caption_court || !caption.caption_plaintiff || !caption.caption_defendant) {
      setPleadError('Fill in the court, plaintiff, and defendant in the Case Caption card first.');
      setCaptionOpen(true);
      return;
    }
    if (!caption.client_party) {
      setPleadError('Select whether this client is the Plaintiff or the Defendant in the Case Caption card — it drives the document title.');
      setCaptionOpen(true);
      return;
    }
    if (!attorneyIds.length) {
      setPleadError('Select at least one attorney for the signature block.');
      return;
    }
    const label = period ? `${kind}:${period}` : kind;
    setGenerating(label);
    try {
      const { blob, filename } = await adminApi.generateClientPleading(id, kind, {
        period,
        court:      caption.caption_court,
        plaintiff:  caption.caption_plaintiff,
        defendant:  caption.caption_defendant,
        docketNo:   caption.caption_docket_no,
        division:   caption.caption_division,
        clientParty: caption.client_party,
        opposingCounsel: caption.opposing_counsel,
        attorneyIds,
        signingAttorneyId,
        saveCaption: true, // generating keeps the stored caption current
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setPleadStatus(`Generated ${filename}`);
    } catch (err) { setPleadError(err.message); }
    finally { setGenerating(''); }
  }

  // pkg144 — save the adoption attorney-fill panel.
  async function saveAdoptFill() {
    setAdoptSavingFill(true); setAdoptFillStatus(''); setPleadError('');
    try {
      await adminApi.saveAdoptionAttorneyData(id, adoptFill);
      setAdoptFillStatus('Saved.');
    } catch (err) { setPleadError(err.message); }
    finally { setAdoptSavingFill(false); }
  }

  // pkg144 — generate one of the four adoption documents. The fill panel is
  // saved first so the document always builds from what's on screen; a 409
  // ICWA_HOLD refreshes the meta so the hold banner appears.
  async function generateAdoptionDoc(kind) {
    setPleadError(''); setPleadStatus('');
    setGenerating(`adoption:${kind}`);
    try {
      await adminApi.saveAdoptionAttorneyData(id, adoptFill);
      const { blob, filename } = await adminApi.generateAdoptionDoc(id, kind, {
        signingAttorneyId: adoptSigner || undefined,
      });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      setPleadStatus(`Generated ${filename}`);
    } catch (err) {
      setPleadError(err.message);
      if (err.code === 'ICWA_HOLD') {
        adminApi.getClientAdoptionIntake(id).then((rec) => setAdoptMeta(rec || null)).catch(() => {});
      }
    }
    finally { setGenerating(''); }
  }

  if (loading) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>;
  if (error && !client) return <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#B85042' }}>{error}</div>;

  const active = activeKey ? SUPPORTED[activeKey] : null;
  const ActiveComponent = active ? active.component : null;
  const partyWord = caption.client_party === 'defendant' ? "Defendant's" : "Plaintiff's";

  const inputStyle = {};

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <Link to={`/admin/clients/${id}`} style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#2f5496', textDecoration: 'none' }}>
          ← Back to {client.name}
        </Link>
      </div>
      <h1 className="page-title" style={{ marginBottom: 4 }}>Client Modules — {client.name}</h1>
      <p className="page-subtitle" style={{ marginTop: 0 }}>
        These are the modules {client.name} sees in their portal, exactly as they see them.
        Anything you save here appears in their portal immediately; submitting to the firm remains their action.
      </p>

      {error && <div className="alert alert-error">{error}</div>}

      {/* ── Case Caption card ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setCaptionOpen((v) => !v)}>
          <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 14 }}>
            Case Caption
            {caption.caption_plaintiff && caption.caption_defendant && (
              <span style={{ marginLeft: 10, fontWeight: 400, fontSize: 12, color: '#888' }}>
                {caption.caption_plaintiff} vs. {caption.caption_defendant}
                {caption.caption_docket_no ? ` · No. ${caption.caption_docket_no}` : ''}
                {caption.client_party ? ` · client is ${caption.client_party}` : ''}
              </span>
            )}
          </div>
          <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#2f5496' }}>{captionOpen ? 'Hide' : 'Edit'}</span>
        </div>
        {captionOpen && (
          <div style={{ marginTop: 14 }}>
            <div className="form-row">
              <div className="form-group" style={{ flex: 2 }}>
                <label>Court</label>
                <input type="text" value={caption.caption_court} onChange={(e) => setCap('caption_court', e.target.value)}
                  placeholder="e.g. Circuit Court for Hamilton County, Tennessee" style={inputStyle} />
              </div>
              <div className="form-group">
                <label>Docket No.</label>
                <input type="text" value={caption.caption_docket_no} onChange={(e) => setCap('caption_docket_no', e.target.value)} placeholder="e.g. 26-D-1234" />
              </div>
              <div className="form-group" style={{ flex: '0 0 120px' }}>
                <label>Division</label>
                <input type="text" value={caption.caption_division} onChange={(e) => setCap('caption_division', e.target.value)} placeholder="e.g. IV" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Plaintiff</label>
                <input type="text" value={caption.caption_plaintiff} onChange={(e) => setCap('caption_plaintiff', e.target.value)} placeholder="Plaintiff's full name" />
              </div>
              <div className="form-group">
                <label>Defendant</label>
                <input type="text" value={caption.caption_defendant} onChange={(e) => setCap('caption_defendant', e.target.value)} placeholder="Defendant's full name" />
              </div>
              <div className="form-group" style={{ flex: '0 0 180px' }}>
                <label>This Client Is The</label>
                <select value={caption.client_party} onChange={(e) => setCap('client_party', e.target.value)}>
                  <option value="">— Select —</option>
                  <option value="plaintiff">Plaintiff</option>
                  <option value="defendant">Defendant</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Opposing Counsel <span style={{ fontWeight: 400, color: '#888' }}>(name and address — used in the Certificate of Service)</span></label>
              <textarea value={caption.opposing_counsel} onChange={(e) => setCap('opposing_counsel', e.target.value)}
                style={{ minHeight: 60 }} placeholder={'Jane Lawyer, Esq.\n123 Main St\nChattanooga, TN 37402'} />
            </div>
            <div className="btn-row" style={{ alignItems: 'center' }}>
              <button className="btn btn-primary" onClick={handleSaveCaption} disabled={savingCaption}>
                {savingCaption ? 'Saving…' : 'Save Caption'}
              </button>
              {captionStatus && <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#1a7a5e' }}>{captionStatus}</span>}
            </div>
          </div>
        )}
      </div>

      {/* ── Module tabs ───────────────────────────────────────────────── */}
      {tabs.length === 0 ? (
        <div className="card">
          <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', margin: 0 }}>
            No built-in modules are assigned to this client. Assign modules from the{' '}
            <Link to={`/admin/clients/${id}?tab=modules`}>Modules tab</Link>.
          </p>
        </div>
      ) : (
        <>
          <div className="client-detail-tabs" style={{ display: 'flex', gap: 4, borderBottom: '2px solid #0F1838', marginBottom: 16, overflowX: 'auto' }}>
            {tabs.map((t) => (
              <button key={t.key}
                onClick={() => {
                  setActiveKey(t.key); setPleadError(''); setPleadStatus('');
                  const next = new URLSearchParams(searchParams);
                  next.set('m', t.key);
                  setSearchParams(next, { replace: true });
                }}
                style={{
                  border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 600,
                  padding: '9px 14px', borderRadius: '4px 4px 0 0',
                  background: activeKey === t.key ? '#0F1838' : '#eee',
                  color: activeKey === t.key ? '#fff' : '#555',
                }}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Pleading panel — only on the three pleading-capable tabs */}
          {active && active.pleading && (
            <div className="card" style={{ marginBottom: 16, background: '#F5F1E8' }}>
              <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 14, marginBottom: 6 }}>
                Generate Pleading
              </div>
              <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555', margin: '0 0 10px' }}>
                Produces a Word document from the saved data below with the case caption, the firm signature block, and a Certificate of Service — ready to review and file.
                {active.pleading === 'income-expense' && ` The worksheet prints one column; pick which period to generate.`}
              </p>

              {/* Attorney selection — mirrors the Discovery generator */}
              <div className="form-group">
                <label>Attorneys on Signature Block</label>
                {attorneys.length === 0 ? (
                  <div style={{ padding: 12, border: '1px dashed #ddd', borderRadius: 4, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', background: '#fff' }}>
                    No owners or attorneys are set up yet. Add them in Staff Management.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #ddd', borderRadius: 4, overflow: 'hidden', background: '#fff' }}>
                    {attorneys.map((a, i) => {
                      const checked = attorneyIds.includes(a.id);
                      const missingBar = !a.bar_number;
                      const isSigning = signingAttorneyId === a.id;
                      return (
                        <div key={a.id} style={{
                          display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px',
                          borderBottom: i < attorneys.length - 1 ? '1px solid #eee' : 'none',
                          background: i % 2 === 0 ? '#fff' : '#fafafa'
                        }}>
                          <input type="checkbox" checked={checked} onChange={() => toggleAttorney(a.id)}
                            style={{ width: 16, height: 16, cursor: 'pointer' }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, fontWeight: 500 }}>
                              {a.name}
                              <span style={{ marginLeft: 8, fontSize: 11, color: '#888', textTransform: 'capitalize', fontWeight: 400 }}>({a.role})</span>
                            </div>
                            <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: missingBar ? '#B85042' : '#888', marginTop: 2 }}>
                              {a.bar_number || '⚠ No bar number on file — cannot sign filings'}
                            </div>
                          </div>
                          {checked && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: 11, color: isSigning ? '#0F1838' : '#888' }}>
                              <input type="radio" name="pleadingSigner" checked={isSigning}
                                onChange={() => setSigningAttorneyId(a.id)} />
                              Signing
                            </label>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {pleadError && <div className="alert alert-error">{pleadError}</div>}
              {pleadStatus && <div className="alert alert-success">{pleadStatus}</div>}

              <div className="btn-row">
                {active.pleading === 'income-expense' ? (
                  <>
                    <button className="btn btn-primary" disabled={!!generating}
                      onClick={() => generatePleading('income-expense', 'pre')}>
                      {generating === 'income-expense:pre' ? 'Generating…' : `Generate Pre-Divorce Worksheet`}
                    </button>
                    <button className="btn btn-primary" disabled={!!generating}
                      onClick={() => generatePleading('income-expense', 'post')}>
                      {generating === 'income-expense:post' ? 'Generating…' : `Generate Post-Divorce Worksheet`}
                    </button>
                  </>
                ) : (
                  <button className="btn btn-primary" disabled={!!generating}
                    onClick={() => generatePleading(active.pleading)}>
                    {generating ? 'Generating…'
                      : active.pleading === 'witness-list'
                        ? `Generate ${partyWord} Witness List`
                        : `Generate ${partyWord} Asset and Liability List`}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* pkg144 — Adoption Documents panel (adoption_stepparent only):
              signing attorney, attorney-fill fields (fees / judge / hearing /
              filed), the four generators, and the ICWA hold banner. Caption
              docket/division come from the Case Caption card above; the
              filing county comes from the client's intake answers. */}
          {active && active.adoption && (() => {
            const icwaHold = adoptMeta && adoptMeta.data && adoptMeta.data.icwa === 'Yes';
            const is14 = !!(adoptMeta && adoptMeta.child_is_14);
            const signerOptions = attorneys.filter((a) => a.bar_number);
            return (
              <div className="card" style={{ marginBottom: 16, background: '#F5F1E8' }}>
                <div style={{ fontWeight: 600, fontFamily: 'Arial, sans-serif', fontSize: 14, marginBottom: 6 }}>
                  Adoption Documents
                </div>
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#555', margin: '0 0 10px' }}>
                  Generates the stepparent adoption filings from the intake answers below. The court, docket, and
                  division come from the Case Caption card; the filing county comes from the client's answers.
                  {adoptMeta && adoptMeta.submitted_at
                    ? ' Intake submitted — ready to generate.'
                    : ' The client has not submitted the intake yet — documents build from whatever is saved below.'}
                </p>

                {icwaHold && (
                  <div style={{ border: '1px solid #e5c07b', background: '#fdf6e3', borderRadius: 6, padding: '10px 14px', marginBottom: 12 }}>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, fontWeight: 600, color: '#7a5b0f' }}>ICWA hold</div>
                    <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#8a6d1f', marginTop: 2 }}>
                      The intake indicates the child may be an Indian child under 25 U.S.C. § 1901 et seq.
                      Standard documents are withheld — this case requires ICWA procedures. The intake answers remain viewable below.
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label>Signing Attorney</label>
                  {signerOptions.length === 0 ? (
                    <div style={{ padding: 12, border: '1px dashed #ddd', borderRadius: 4, fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#888', background: '#fff' }}>
                      No owners or attorneys with a bar number on file. Add one in Staff Management.
                    </div>
                  ) : (
                    <select value={adoptSigner || ''} onChange={(e) => setAdoptSigner(e.target.value || null)}>
                      <option value="">Default — first owner with a bar number</option>
                      {signerOptions.map((a) => (
                        <option key={a.id} value={a.id}>{a.name} (BPR #{a.bar_number})</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Filing Fee ($)</label>
                    <input type="number" step="0.01" value={adoptFill.filing_fee} onChange={(e) => { setAdoptFill((f) => ({ ...f, filing_fee: e.target.value })); setAdoptFillStatus(''); }} />
                  </div>
                  <div className="form-group">
                    <label>Attorney Fee ($)</label>
                    <input type="number" step="0.01" value={adoptFill.attorney_fee} onChange={(e) => { setAdoptFill((f) => ({ ...f, attorney_fee: e.target.value })); setAdoptFillStatus(''); }} />
                  </div>
                  <div className="form-group">
                    <label>Judge (decree)</label>
                    <input type="text" value={adoptFill.judge_name} onChange={(e) => { setAdoptFill((f) => ({ ...f, judge_name: e.target.value })); setAdoptFillStatus(''); }} />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Petition Filed (decree)</label>
                    <input type="date" value={adoptFill.petition_filed_date} onChange={(e) => { setAdoptFill((f) => ({ ...f, petition_filed_date: e.target.value })); setAdoptFillStatus(''); }} />
                  </div>
                  <div className="form-group">
                    <label>Hearing Date (decree)</label>
                    <input type="date" value={adoptFill.hearing_date} onChange={(e) => { setAdoptFill((f) => ({ ...f, hearing_date: e.target.value })); setAdoptFillStatus(''); }} />
                  </div>
                  <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button className="btn" onClick={saveAdoptFill} disabled={adoptSavingFill}>
                      {adoptSavingFill ? 'Saving…' : 'Save Details'}
                    </button>
                    {adoptFillStatus && <span style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#1a7a5e', marginLeft: 10, alignSelf: 'center' }}>{adoptFillStatus}</span>}
                  </div>
                </div>

                {pleadError && <div className="alert alert-error">{pleadError}</div>}
                {pleadStatus && <div className="alert alert-success">{pleadStatus}</div>}

                <div className="btn-row" style={{ flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" disabled={!!generating || icwaHold} onClick={() => generateAdoptionDoc('petition')}>
                    {generating === 'adoption:petition' ? 'Generating…' : 'Generate Petition'}
                  </button>
                  <button className="btn btn-primary" disabled={!!generating || icwaHold} onClick={() => generateAdoptionDoc('pfr')}>
                    {generating === 'adoption:pfr' ? 'Generating…' : 'Generate Registry Inquiry'}
                  </button>
                  {is14 && (
                    <button className="btn btn-primary" disabled={!!generating || icwaHold} onClick={() => generateAdoptionDoc('consent')}>
                      {generating === 'adoption:consent' ? 'Generating…' : 'Generate Consent of Child (14+)'}
                    </button>
                  )}
                  <button className="btn btn-primary" disabled={!!generating || icwaHold} onClick={() => generateAdoptionDoc('decree')}>
                    {generating === 'adoption:decree' ? 'Generating…' : 'Generate Final Decree'}
                  </button>
                </div>
                {is14 && (
                  <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#888', margin: '8px 0 0' }}>
                    The child is 14 or older based on the date of birth in the intake — the Consent of Child is included.
                  </p>
                )}
              </div>
            );
          })()}

          {/* pkg127 (D-126c) — download / email-to-staff for the active
              module, right where the firm is working it. child_support_calc
              is stateless (D-126d): nothing to download or send. */}
          {active && !active.stateless && (() => {
            const dl = (fname, fn) => async () => {
              try {
                const blob = await fn(id);
                const a = document.createElement('a');
                a.href = URL.createObjectURL(blob);
                a.download = fname.replace('{c}', (client.name || 'Client').replace(/\s+/g, '_'));
                a.click();
              } catch (err) { setError(err.message); }
            };
            const SEND_MAP = {
              demographic:    { label: 'Download .pdf',  onDownload: dl('Divorce_Certificate_{c}.pdf', adminApi.downloadDivorceCertificate), send: (ids) => adminApi.sendClientDemographic(id, ids) },
              parenting_plan: { label: 'Download .docx', onDownload: dl('Parenting_Plan_{c}.docx', adminApi.downloadParentingPlan),          send: (ids) => adminApi.sendClientParentingPlan(id, ids) },
              witnesses:      { label: 'Download .docx', onDownload: dl('Witness_List_{c}.docx', adminApi.downloadClientWitnesses),          send: (ids) => adminApi.sendClientWitnesses(id, ids) },
              budget:         { label: 'Download .xlsx', onDownload: dl('Budget_Worksheet_{c}.xlsx', adminApi.downloadClientBudget),         send: (ids) => adminApi.sendClientBudget(id, ids) },
              juvenile_info:  { label: 'Download .pdf',  onDownload: dl('Juvenile_Court_Info_{c}.pdf', adminApi.downloadJuvenileInfo),       send: (ids) => adminApi.sendClientJuvenileInfo(id, ids) },
              asset_ledger:   { label: 'Download .docx', onDownload: dl('Asset_Debt_List_{c}.docx', adminApi.downloadClientAssetLedger),     send: (ids) => adminApi.sendClientAssetLedger(id, ids) },
            };
            const m = SEND_MAP[activeKey];
            if (!m) return null;
            return (
              <div className="card" style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ fontFamily: 'Arial, sans-serif', fontSize: 13, color: '#555' }}>
                  Download this module's document or email it to staff. Save your edits below first — documents build from saved data.
                </div>
                <ModuleSendControl
                  compact
                  staff={allStaff}
                  downloadLabel={m.label}
                  onDownload={m.onDownload}
                  onSend={m.send} />
              </div>
            );
          })()}

          {/* The client's actual module page, admin mode */}
          {ActiveComponent && (
            <div className="card">
              {active.stateless && (
                <p style={{ fontFamily: 'Arial, sans-serif', fontSize: 12, color: '#888', marginTop: 0 }}>
                  The calculator runs entirely in the browser — nothing is saved, for you or the client.
                </p>
              )}
              <ActiveComponent key={activeKey} {...(active.stateless ? {} : { admin: { clientId: id } })} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
